import { useEffect, useRef, type RefObject } from 'react';

// UX-77 — extrait TEL QUEL du mécanisme posé par `Modal.tsx` (UX-22), jamais
// dupliqué : `Modal` consomme désormais ce hook lui-même (voir Modal.tsx).
// Piège de focus BASIQUE (accessibilité minimale demandée, pas une librairie
// complète) : focus initial sur le premier élément focusable, cycle
// Tab/Shift+Tab entre premier et dernier, Échap ferme, restauration du focus
// au démontage — voir le commentaire de tête de `Modal.tsx` pour la
// justification d'origine de ce choix (pas de dépendance externe).
const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Piège de focus + fermeture Échap pour un `role="dialog"` (modale ou tiroir).
 * Retourne une ref à poser sur le conteneur du dialogue — remplacement direct
 * de la `dialogRef` que chaque appelant créait lui-même auparavant.
 *
 * ⚠️ **Empilement de dialogues (UX-77, ex-tiroir `EnrollmentDrawer` — page depuis UX-81 — + `AssignClassModal`/
 * `RejectModal`/`Modal` de rejet de document — vérifié réellement, pas
 * supposé, voir docs/history/UX.md)** : `document.addEventListener('keydown', ...)`
 * pose UN listener PAR dialogue monté ; sans garde, Échap fermerait TOUS les
 * dialogues empilés simultanément (les listeners du même type sur `document`
 * s'exécutent tous, `stopPropagation` ne les arrête pas entre eux). Avant de
 * traiter `Escape`/`Tab`, ce hook vérifie donc que son propre conteneur est
 * bien le DERNIER `[role="dialog"]` du DOM — un dialogue empilé EN DESSOUS
 * d'un autre devient inerte au clavier tant que l'autre reste ouvert. Vérifié
 * réellement (rendu + ouverture des deux + inspection de l'ordre du
 * NodeList) sur les 3 combinaisons réelles de ce projet : l'ordre DOM
 * correspond toujours à l'ordre d'ouverture, aucun portail n'est utilisé nulle
 * part dans ce monorepo.
 */
export function useFocusTrap<T extends HTMLElement = HTMLElement>(onClose: () => void): RefObject<T | null> {
  const dialogRef = useRef<T>(null);

  // `onClose` capturé par référence MOBILE (pas dans le tableau de dépendances
  // de l'effet ci-dessous) — même raison que `Modal.tsx` d'origine : l'effet
  // ne doit tourner qu'UNE FOIS par montage, jamais se ré-exécuter si
  // l'appelant passe une nouvelle fonction `onClose` à chaque rendu.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;

    function getFocusable(): HTMLElement[] {
      if (!dialog) return [];
      return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    }

    // Focus initial posé sur le premier élément focusable du dialogue —
    // jamais le conteneur lui-même, qui n'est pas nativement focusable sans
    // `tabIndex` et serait moins utile qu'un vrai élément actionnable au
    // clavier dès l'ouverture.
    getFocusable()[0]?.focus();

    function isTopmostDialog(): boolean {
      if (!dialog) return false;
      const dialogs = document.querySelectorAll('[role="dialog"]');
      return dialogs.length > 0 && dialogs[dialogs.length - 1] === dialog;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (!isTopmostDialog()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      // Piège de focus BASIQUE : cycle entre le premier et le dernier élément
      // focusable du dialogue — le focus ne s'échappe jamais vers le reste de
      // la page tant qu'il est ouvert.
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Rend le focus à l'élément qui l'avait avant l'ouverture — pas
      // seulement une bonne pratique d'accessibilité, évite aussi qu'un `Tab`
      // suivant reparte du tout début de la page.
      previouslyFocused?.focus();
    };
  }, []);

  return dialogRef;
}
